import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * E-E-A-T signal for clinical content pages.
 *
 * Renders a visible "Medically reviewed" / "Last updated" line sourced from
 * real content metadata. No dates or reviewer names are fabricated — the
 * component only renders fields that are explicitly provided by the page.
 *
 * If neither `reviewedBy` nor `lastUpdated` is supplied, nothing renders
 * (the slot exists in every clinical template and is ready to populate).
 */
export interface ClinicalReviewNoteProps {
  /** Named reviewer + credentials, e.g. "Daniel Pietras, MD". */
  reviewedBy?: string;
  /** ISO 8601 date string, e.g. "2026-08-28". */
  lastUpdated?: string;
  className?: string;
}

export const ClinicalReviewNote: React.FC<ClinicalReviewNoteProps> = ({
  reviewedBy,
  lastUpdated,
  className = "",
}) => {
  if (!reviewedBy && !lastUpdated) return null;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <p
      className={`flex items-center gap-2 text-xs sm:text-sm text-muted-foreground ${className}`}
    >
      <ShieldCheck className="h-4 w-4 shrink-0 text-accent" />
      <span>
        {reviewedBy && <>Medically reviewed by {reviewedBy}.</>}{" "}
        {lastUpdated && <>Last updated {formatDate(lastUpdated)}.</>}
      </span>
    </p>
  );
};

export default ClinicalReviewNote;
